import { useState, useMemo } from 'react'
import { ltvLtcText } from '@/pages/pipeline-intelligence/dealResolution'
import { withDemo } from '@/config/demoMode'
import {
  DealTableRow,
  DealTableSortHeader,
  dealTableHeadRowClass,
  useDealTableInteraction,
  useLoanTableSort,
} from '@/components/shared/DealTable'
import {
  Building2,
  TrendingUp,
  DollarSign,
  FolderKanban,
  HardHat,
  Receipt,
  ChevronRight,
  AlertTriangle,
  Eye,
  ArrowUpRight,
  ArrowDownRight,
  Minus,
  Target,
} from 'lucide-react'
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  Legend,
} from 'recharts'
import type { Loan } from '@/types'
import { useAntarcticaDeals, useDevelopmentProjects, useModuleDemoEnabled } from '@/hooks/useDemoDataAccess'
import { analyticsLoan } from '@/pages/liveModuleData'
import {
  CHART_LEGEND_STYLE,
  CHART_TOOLTIP_PROPS,
  chartAxisProps,
  chartGridProps,
  chartPieColor,
  chartSemanticProgressColor,
  chartSeriesColor,
  chartStackSegmentColor,
} from '@/config/chartTheme'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmt = (n: number, decimals = 1) =>
  n >= 1000 ? `$${(n / 1000).toFixed(decimals)}B` : `$${n.toFixed(decimals)}M`

const pct = (n: number, decimals = 1) => `${(n * 100).toFixed(decimals)}%`

type Tab = 'overview' | 'feasibility' | 'budget' | 'focus-list'

// ---------------------------------------------------------------------------
// Budget mock data
// ---------------------------------------------------------------------------

// Register I7 - illustrative only. On a live account these rendered as this
// client's own construction budget, beside their real deals.
const DEMO_BUDGET_ITEMS = [
  { category: 'Land Acquisition', budget: 42.0, actual: 43.2, pctComplete: 100 },
  { category: 'Hard Costs', budget: 128.5, actual: 118.9, pctComplete: 72 },
  { category: 'Soft Costs', budget: 22.0, actual: 19.4, pctComplete: 65 },
  { category: 'Contingency', budget: 15.0, actual: 4.8, pctComplete: 32 },
  { category: 'Financing Costs', budget: 18.5, actual: 12.1, pctComplete: 58 },
]

// Register I7. The "Current Forecast" series was generated with Math.random(),
// so a live account saw a different 24-month draw curve on every page load,
// presented as this client's own cash flow.
const DEMO_CASH_FLOW_MONTHS = (() => {
  const months: { month: string; budget: number; forecast: number }[] = []
  const labels = [
    'Jan 25', 'Feb 25', 'Mar 25', 'Apr 25', 'May 25', 'Jun 25',
    'Jul 25', 'Aug 25', 'Sep 25', 'Oct 25', 'Nov 25', 'Dec 25',
    'Jan 26', 'Feb 26', 'Mar 26', 'Apr 26', 'May 26', 'Jun 26',
    'Jul 26', 'Aug 26', 'Sep 26', 'Oct 26', 'Nov 26', 'Dec 26',
  ]
  let budgetCum = 0
  let forecastCum = 0
  for (let i = 0; i < labels.length; i++) {
    const budgetDraw = 8.5 + Math.sin(i * 0.5) * 3.2
    const forecastDraw = budgetDraw * (1 + (Math.random() * 0.12 - 0.04))
    budgetCum += budgetDraw
    forecastCum += forecastDraw
    months.push({
      month: labels[i],
      budget: Math.round(budgetCum * 10) / 10,
      forecast: Math.round(forecastCum * 10) / 10,
    })
  }
  return months
})()

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AssetManagement() {
    const developmentProjects = useDevelopmentProjects('asset-management')
  const deals = useAntarcticaDeals('asset-management')
  const demoEnabled = useModuleDemoEnabled('asset-management')
  const [tab, setTab] = useState<Tab>('overview')

  // Funded / asset-management deals. Live loans are in dollars with value and
  // NOI in market_data / cash_flows / custom fields; read them there (in $MM)
  // rather than from `propertyValue` / `noi`, which a live loan never carries
  // — that is why Monitoring showed $0.0M AUM for loans Surveillance listed
  // at $41.3M and $52.8M (MOD-24).
  const managedDeals = useMemo(() => {
    const funded = deals.filter((d) => d.stage === 'Funded')
    return demoEnabled ? funded : funded.map(analyticsLoan)
  }, [deals, demoEnabled])

  // Metrics — over the loans that record the figure; null when none do.
  const totalAum = useMemo(() => {
    const valued = managedDeals.filter((d) => typeof d.propertyValue === 'number')
    return valued.length ? valued.reduce((s, d) => s + (d.propertyValue ?? 0), 0) : null
  }, [managedDeals])
  const portfolioNoi = useMemo(() => {
    const withNoi = managedDeals.filter((d) => typeof d.noi === 'number')
    return withNoi.length ? withNoi.reduce((s, d) => s + (d.noi ?? 0), 0) : null
  }, [managedDeals])
  // Register I7. This was `14.8 // mock weighted average`, rendered as the
  // "Wtd Avg IRR" KPI on a live account. Nothing computes a portfolio IRR yet,
  // and a plausible invented one is indistinguishable from a real figure.
  const wtdAvgIrr = null as number | null
  const activeProjects = developmentProjects.length

  // Portfolio composition by type
  const compositionByType = useMemo(() => {
    const map = new Map<string, number>()
    managedDeals.forEach((d) => {
      map.set(d.type, (map.get(d.type) ?? 0) + (d.propertyValue ?? 0))
    })
    return Array.from(map.entries()).map(([name, value]) => ({ name, value: Math.round(value * 10) / 10 }))
  }, [managedDeals])

  // NOI by property
  const noiByProperty = useMemo(
    () =>
      managedDeals.map((d) => ({
        name: d.property.length > 20 ? d.property.slice(0, 18) + '...' : d.property,
        noi: d.noi ?? 0,
      })),
    [managedDeals],
  )

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Portfolio Overview', icon: <Building2 size={13} /> },
    { id: 'focus-list', label: 'Focus List', icon: <Target size={13} /> },
    { id: 'feasibility', label: 'Development Feasibility', icon: <HardHat size={13} /> },
    { id: 'budget', label: 'Budget Mgmt', icon: <Receipt size={13} /> },
  ]

  return (
    <div className="page-fill gap-2 px-3 py-2">
      {/* ── Metrics Strip ─────────────────────────────────────────────── */}
      <div className="grid flex-shrink-0 grid-cols-4 gap-2">
        <MetricCard icon={<DollarSign size={14} />} label="Property Value on Record" value={totalAum == null ? '—' : fmt(totalAum)} />
        <MetricCard icon={<TrendingUp size={14} />} label="Wtd Avg IRR" value={wtdAvgIrr === null ? '—' : `${wtdAvgIrr.toFixed(1)}%`} />
        <MetricCard icon={<DollarSign size={14} />} label="Portfolio NOI" value={portfolioNoi == null ? '—' : fmt(portfolioNoi)} />
        <MetricCard icon={<FolderKanban size={14} />} label="Active Projects" value={String(activeProjects)} />
      </div>

      {/* ── Tab Bar ───────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 gap-1">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-medium tracking-wide transition-colors ${
              tab === t.id
                ? 'bg-amber-400/[0.10] text-amber-300 border border-amber-400/20'
                : 'text-stone-500 hover:text-stone-300 border border-transparent'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab Content ───────────────────────────────────────────────── */}
      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'overview' && (
          <div className="h-full overflow-y-auto scrollbar-thin">
            <PortfolioOverview deals={managedDeals} compositionByType={compositionByType} noiByProperty={noiByProperty} graded={demoEnabled} />
          </div>
        )}
        {tab === 'focus-list' && <FocusListTab deals={managedDeals} />}
        {tab === 'feasibility' && (
          <div className="h-full overflow-y-auto scrollbar-thin">
            <DevelopmentFeasibilityTab />
          </div>
        )}
        {tab === 'budget' && (
          <div className="h-full overflow-y-auto scrollbar-thin">
            <BudgetManagement />
          </div>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Metric Card
// ---------------------------------------------------------------------------

function MetricCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="metric-cell">
      <div className="flex items-center gap-1.5 metric-cell-label">
        {icon}
        {label}
      </div>
      <div className="metric-cell-value">{value}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 1 — Portfolio Overview
// ---------------------------------------------------------------------------

function PortfolioOverview({
  deals,
  compositionByType,
  noiByProperty,
  graded,
}: {
  deals: Loan[]
  compositionByType: { name: string; value: number }[]
  noiByProperty: { name: string; noi: number }[]
  /** Demo rows carry a curated status; live loans have no covenant test on record. */
  graded: boolean
}) {
  const { focusedDealId, activateDeal } = useDealTableInteraction()
  const { sortKey, sortDir, toggleSort, sortedLoans } = useLoanTableSort(deals, 'propertyValue', 'desc')

  return (
    <div className="space-y-3">
      {/* Table */}
      <div className="panel overflow-x-auto">
        <div className="panel-header">
          <span className="panel-header-title">Portfolio Summary</span>
          <span className="text-[10px] text-stone-600 font-mono">{deals.length} assets</span>
        </div>
        <table className="dense-table">
          <thead>
            <tr className={dealTableHeadRowClass()}>
              <DealTableSortHeader label="Property" col="property" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <DealTableSortHeader label="Type" col="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <DealTableSortHeader label="Location" col="location" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <DealTableSortHeader label="Value ($M)" col="propertyValue" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <DealTableSortHeader label="NOI ($M)" col="noi" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <DealTableSortHeader label="Cap Rate" col="capRate" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <DealTableSortHeader label="DSCR" col="dscr" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <DealTableSortHeader label="LTV / LTC" col="ltvLtc" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} align="right" />
              <DealTableSortHeader label="Status" col="stage" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
            </tr>
          </thead>
          <tbody>
            {sortedLoans.map((d) => (
              <DealTableRow
                key={d.id}
                dealId={d.id}
                isFocused={focusedDealId === d.id}
                onActivate={activateDeal}
                openProfileOnClick={false}
              >
                <td className="text-stone-200 font-medium">{d.property}</td>
                <td>{d.type}</td>
                <td className="text-stone-400">{d.location}</td>
                <td className="text-right tabular-nums">{typeof d.propertyValue === 'number' ? d.propertyValue.toFixed(1) : '—'}</td>
                <td className="text-right tabular-nums">{typeof d.noi === 'number' ? d.noi.toFixed(2) : '—'}</td>
                <td className="text-right tabular-nums">
                  {d.propertyValue && d.noi ? ((d.noi / d.propertyValue) * 100).toFixed(2) + '%' : '—'}
                </td>
                <td className="text-right tabular-nums">{typeof d.dscr === 'number' ? `${d.dscr.toFixed(2)}x` : '—'}</td>
                {/* LTV-else-LTC, as the Pipeline prints it: the book states most
                    funded loans' leverage as one In Place LTV/LTC figure, and
                    reading `ltv` alone left all 36 blank. */}
                <td className="text-right tabular-nums">{ltvLtcText(d, 0) ?? '—'}</td>
                <td>
                  {graded ? (
                    <span className="inline-flex items-center gap-1 text-emerald-400 text-[11px]">
                      <span className="status-dot bg-emerald-400" />
                      Performing
                    </span>
                  ) : (
                    // Same answer as Surveillance: no covenant test is on
                    // record, so the loan is not graded.
                    <span className="text-[11px] text-stone-500" title="No covenant test on record">
                      Not graded
                    </span>
                  )}
                </td>
              </DealTableRow>
            ))}
          </tbody>
        </table>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-2 gap-3">
        {/* Pie — Composition by Type */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">Portfolio by Property Type</span>
          </div>
          <div className="panel-body h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={compositionByType}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={2}
                  stroke="none"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                >
                  {compositionByType.map((_, i) => (
                    <Cell key={i} fill={chartPieColor(i)} />
                  ))}
                </Pie>
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`$${v.toFixed(1)}M`, 'Value']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Bar — NOI by Property */}
        <div className="panel">
          <div className="panel-header">
            <span className="panel-header-title">NOI by Property ($M)</span>
          </div>
          <div className="panel-body h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={noiByProperty} layout="vertical" margin={{ left: 10, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid {...chartGridProps(false)} />
                <XAxis type="number" {...chartAxisProps()} />
                <YAxis type="category" dataKey="name" {...chartAxisProps()} width={120} />
                <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`$${v.toFixed(2)}M`, 'NOI']} />
                <Bar dataKey="noi" radius={[0, 4, 4, 0]} barSize={18}>
                  {noiByProperty.map((_, i) => (
                    <Cell key={i} fill={chartSeriesColor(i)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2 — Development Feasibility
// ---------------------------------------------------------------------------

function DevelopmentFeasibilityTab() {
  const developmentProjects = useDevelopmentProjects('asset-management')
  return (
    <div className="space-y-3">
      {developmentProjects.map((p, idx) => {
        const marginColor = p.margin > 0.2 ? 'text-emerald-400' : p.margin > 0.1 ? 'text-amber-400' : 'text-red-400'
        const equityPct = (p.equityRequired / p.totalCost) * 100
        const debtPct = (p.debtRequired / p.totalCost) * 100

        return (
          <div key={idx} className="panel">
            <div className="panel-header">
              <div className="flex items-center gap-2">
                <HardHat size={13} className="text-amber-300" />
                <span className="panel-header-title">{p.projectName}</span>
              </div>
              <span className="text-[10px] text-stone-500 font-mono">
                {p.constructionPeriod} mo construction
              </span>
            </div>
            <div className="panel-body space-y-3">
              {/* Key metrics row */}
              <div className="grid grid-cols-6 gap-3">
                <MiniMetric label="Total Cost" value={fmt(p.totalCost)} />
                <MiniMetric label="GDV" value={fmt(p.gdv)} />
                <MiniMetric label="Dev Margin" value={pct(p.margin)} className={marginColor} />
                <MiniMetric label="IRR" value={`${p.irr.toFixed(1)}%`} />
                <MiniMetric label="NPV" value={`$${p.npv.toFixed(1)}M`} className={p.npv >= 0 ? 'text-emerald-400' : 'text-red-400'} />
                <MiniMetric label="Profit on Cost" value={pct(p.profitOnCost)} />
              </div>

              {/* Equity / Debt split bar */}
              <div>
                <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-widest text-stone-500 mb-1">
                  <span>Equity: ${p.equityRequired.toFixed(1)}M ({equityPct.toFixed(0)}%)</span>
                  <span>Debt: ${p.debtRequired.toFixed(1)}M ({debtPct.toFixed(0)}%)</span>
                </div>
                <div className="chart-viz-track flex h-2 overflow-hidden rounded-full">
                  <div
                    className="chart-viz-bar h-full"
                    style={{ width: `${equityPct}%`, background: chartStackSegmentColor(0) }}
                  />
                  <div
                    className="chart-viz-bar h-full"
                    style={{ width: `${debtPct}%`, background: chartStackSegmentColor(1) }}
                  />
                </div>
              </div>

              {/* Scenario table */}
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Scenario</th>
                    <th className="text-right">Margin</th>
                    <th className="text-right">IRR</th>
                    <th className="text-right">NPV ($M)</th>
                  </tr>
                </thead>
                <tbody>
                  {p.scenarios.map((s, si) => {
                    const mColor = s.margin > 0.2 ? 'text-emerald-400' : s.margin > 0.1 ? 'text-amber-400' : 'text-red-400'
                    return (
                      <tr key={si}>
                        <td className="text-stone-200">{s.name}</td>
                        <td className={`text-right tabular-nums ${mColor}`}>{pct(s.margin)}</td>
                        <td className="text-right tabular-nums">{s.irr.toFixed(1)}%</td>
                        <td className={`text-right tabular-nums ${s.npv >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                          {s.npv >= 0 ? '+' : ''}{s.npv.toFixed(1)}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>

              {/* Construction period indicator */}
              <div className="flex items-center gap-2 text-[10px] text-stone-500 font-mono">
                <ChevronRight size={11} className="text-amber-400/60" />
                <span>Construction: {p.constructionPeriod} months</span>
                <div className="flex-1 h-px bg-amber-400/10" />
                <div className="flex gap-px">
                  {Array.from({ length: Math.ceil(p.constructionPeriod / 6) }).map((_, i) => (
                    <div
                      key={i}
                      className="chart-viz-bar chart-viz-bar--rounded-sm h-1.5 w-4"
                      style={{ background: chartSeriesColor(i % 4) }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function MiniMetric({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-stone-600">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${className ?? 'text-stone-200'}`}>{value}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 3 — Budget Management
// ---------------------------------------------------------------------------

function BudgetManagement() {
  // Gated here, at render, so the module's own demo switch governs it too.
  const BUDGET_ITEMS = withDemo(DEMO_BUDGET_ITEMS, [], 'asset-management')
  const CASH_FLOW_MONTHS = withDemo(DEMO_CASH_FLOW_MONTHS, [], 'asset-management')
  const totalBudget = BUDGET_ITEMS.reduce((s, b) => s + b.budget, 0)
  const totalActual = BUDGET_ITEMS.reduce((s, b) => s + b.actual, 0)
  const totalVariance = totalBudget - totalActual
  // A live account has no construction budget on record; totals over an empty
  // list read "Net Variance: +$0.0M" and a 0.0 total row (MOD-24).
  const hasBudget = BUDGET_ITEMS.length > 0

  return (
    <div className="space-y-3">
      {/* Budget vs Actual Grid */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Budget vs Actual Tracking</span>
          {hasBudget ? (
            <span className={`text-[10px] font-mono ${totalVariance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              Net Variance: {totalVariance >= 0 ? '+' : ''}${totalVariance.toFixed(1)}M
            </span>
          ) : (
            <span className="text-[10px] font-mono text-stone-500">Net Variance: —</span>
          )}
        </div>
        <table className="dense-table">
          <thead>
            <tr>
              <th>Category</th>
              <th className="text-right">Budget ($M)</th>
              <th className="text-right">Actual ($M)</th>
              <th className="text-right">Variance ($M)</th>
              <th className="text-right">% Complete</th>
              <th className="w-32">Progress</th>
            </tr>
          </thead>
          <tbody>
            {BUDGET_ITEMS.map((b, i) => {
              const variance = b.budget - b.actual
              const isUnder = variance >= 0
              return (
                <tr key={i}>
                  <td className="text-stone-200 font-medium">{b.category}</td>
                  <td className="text-right tabular-nums">{b.budget.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{b.actual.toFixed(1)}</td>
                  <td className={`text-right tabular-nums ${isUnder ? 'text-emerald-400' : 'text-red-400'}`}>
                    {isUnder ? '+' : ''}{variance.toFixed(1)}
                  </td>
                  <td className="text-right tabular-nums">{b.pctComplete}%</td>
                  <td>
                    <div className="chart-viz-track chart-viz-bar--rounded h-1.5">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full"
                        style={{
                          width: `${b.pctComplete}%`,
                          background: chartSemanticProgressColor(b.pctComplete, { warn: 50, good: 90 }),
                        }}
                      />
                    </div>
                  </td>
                </tr>
              )
            })}
            {!hasBudget && (
              <tr>
                <td colSpan={6} className="py-6 text-center text-[11px] text-stone-500">
                  No construction budget recorded.
                </td>
              </tr>
            )}
            {/* Totals row */}
            {hasBudget && (
            <tr className="border-t border-amber-400/20">
              <td className="text-amber-300 font-semibold">Total</td>
              <td className="text-right tabular-nums text-amber-300 font-semibold">{totalBudget.toFixed(1)}</td>
              <td className="text-right tabular-nums text-amber-300 font-semibold">{totalActual.toFixed(1)}</td>
              <td className={`text-right tabular-nums font-semibold ${totalVariance >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                {totalVariance >= 0 ? '+' : ''}{totalVariance.toFixed(1)}
              </td>
              <td />
              <td />
            </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Cash Flow Reforecast Chart */}
      <div className="panel">
        <div className="panel-header">
          <span className="panel-header-title">Cash Flow Reforecast — Cumulative Draw ($M)</span>
        </div>
        <div className="panel-body h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={CASH_FLOW_MONTHS} margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
              <CartesianGrid {...chartGridProps()} />
              <XAxis dataKey="month" {...chartAxisProps(9)} interval={2} />
              <YAxis {...chartAxisProps()} />
              <Tooltip {...CHART_TOOLTIP_PROPS} formatter={(v: number) => [`$${v.toFixed(1)}M`]} />
              <Legend verticalAlign="top" height={28} wrapperStyle={CHART_LEGEND_STYLE} />
              <Area
                type="monotone"
                dataKey="budget"
                name="Original Budget"
                stroke={chartSeriesColor(0)}
                strokeWidth={2}
                fill={chartSeriesColor(0)}
              />
              <Area
                type="monotone"
                dataKey="forecast"
                name="Current Forecast"
                stroke={chartSeriesColor(1)}
                strokeWidth={2}
                strokeDasharray="4 3"
                fill={chartSeriesColor(1)}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Focus List — assets flagged for review based on market conditions
// ---------------------------------------------------------------------------

type FocusReason = 'Market Softening' | 'Cap Rate Expansion' | 'Vacancy Spike' | 'NOI Decline' | 'Lease Rollover Risk' | 'Maturity Approaching' | 'Borrower Stress'

interface FocusItem {
  dealId: string
  property: string
  type: string
  location: string
  loanAmount: number
  reason: FocusReason
  /**
   * Register I7. These two carried invented history on LIVE deals: a fixed
   * date, and an "at origination" figure produced by subtracting a constant
   * from the CURRENT value. Nothing stores either, so both are optional now and
   * the row simply omits what is not known rather than inventing a comparison a
   * reviewer would act on.
   */
  addedDate?: string
  severity: 'Critical' | 'Elevated' | 'Watch'
  currentMetric: string
  origMetric?: string
  marketTrend: 'declining' | 'stable' | 'improving'
  notes: string
  actionRequired: string
}

function generateFocusList(deals: import('@/types').Loan[]): FocusItem[] {
  const items: FocusItem[] = []

  deals.forEach((d) => {
    // Market-based triggers (even without covenant breach)
    if (d.market.vacRate > 7) {
      items.push({
        dealId: d.id,
        property: d.property,
        type: d.type,
        location: d.location,
        loanAmount: d.loanAmount,
        reason: 'Vacancy Spike',
        severity: d.market.vacRate > 10 ? 'Critical' : 'Elevated',
        currentMetric: `${d.market.vacRate.toFixed(1)}% vacancy`,
        marketTrend: 'declining',
        notes: 'Market vacancy is elevated. Submarket showing negative absorption.',
        actionRequired: 'Request updated rent roll and financial statements. Evaluate need for reserve adjustment.',
      })
    }

    if (d.market.rentGrowth < 1.0) {
      items.push({
        dealId: d.id,
        property: d.property,
        type: d.type,
        location: d.location,
        loanAmount: d.loanAmount,
        reason: 'Market Softening',
        severity: d.market.rentGrowth < 0 ? 'Critical' : 'Watch',
        currentMetric: `${d.market.rentGrowth.toFixed(1)}% rent growth`,
        marketTrend: d.market.rentGrowth < 0 ? 'declining' : 'stable',
        notes: `Rent growth deceleration in ${d.market.msa}. Monitoring for potential NOI impact.`,
        actionRequired: 'Monitor next quarter. Compare actual vs. UW rent assumptions.',
      })
    }

    if (d.market.capRate > 6.5) {
      items.push({
        dealId: d.id,
        property: d.property,
        type: d.type,
        location: d.location,
        loanAmount: d.loanAmount,
        reason: 'Cap Rate Expansion',
        severity: 'Elevated',
        currentMetric: `${d.market.capRate.toFixed(1)}% market cap rate`,
        marketTrend: 'declining',
        notes: `Cap rate expansion of ~50bp in ${d.type} sector. Value may be impaired.`,
        actionRequired: 'Order updated appraisal. Run stress-loss scenario.',
      })
    }
  })

  // Sort by severity
  const sevOrder = { Critical: 0, Elevated: 1, Watch: 2 }
  return items.sort((a, b) => sevOrder[a.severity] - sevOrder[b.severity])
}

function FocusListTab({ deals }: { deals: import('@/types').Loan[] }) {
  const focusItems = useMemo(() => generateFocusList(deals), [deals])

  const criticalCount = focusItems.filter((f) => f.severity === 'Critical').length
  const elevatedCount = focusItems.filter((f) => f.severity === 'Elevated').length
  const watchCount = focusItems.filter((f) => f.severity === 'Watch').length
  const totalExposure = focusItems.reduce((s, f) => s + f.loanAmount, 0)

  const sevBadge = (sev: FocusItem['severity']) => {
    const cls = sev === 'Critical' ? 'text-red-400 bg-red-400/10 border-red-400/20'
      : sev === 'Elevated' ? 'text-amber-400 bg-amber-400/10 border-amber-400/20'
      : 'text-blue-400 bg-blue-400/10 border-blue-400/20'
    return <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium border ${cls}`}>{sev}</span>
  }

  const trendIcon = (t: FocusItem['marketTrend']) => {
    if (t === 'declining') return <ArrowDownRight size={11} className="text-red-400" />
    if (t === 'improving') return <ArrowUpRight size={11} className="text-emerald-400" />
    return <Minus size={11} className="text-stone-600" />
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-2">
      {/* Description */}
      <div className="flex flex-shrink-0 items-center gap-2 rounded-lg border border-amber-400/10 bg-amber-400/[0.03] px-3 py-1.5">
        <Target size={14} className="text-amber-400 flex-shrink-0" />
        <span className="text-[11px] text-stone-400">
          <span className="font-medium text-amber-300">Focus List:</span> Assets flagged for review based on market conditions, even when no covenant breach has occurred. These require proactive monitoring and may need reserve adjustments or updated valuations.
        </span>
      </div>

      {/* Summary */}
      <div className="grid flex-shrink-0 grid-cols-4 gap-2">
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-red-400/10"><AlertTriangle size={14} className="text-red-400" /></div>
          <div>
            <div className="metric-cell-label">Critical</div>
            <div className="metric-cell-value text-red-400">{criticalCount}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-amber-400/10"><Eye size={14} className="text-amber-400" /></div>
          <div>
            <div className="metric-cell-label">Elevated</div>
            <div className="metric-cell-value text-amber-400">{elevatedCount}</div>
          </div>
        </div>
        <div className="metric-cell flex items-center gap-3">
          <div className="p-1.5 rounded bg-blue-400/10"><Eye size={14} className="text-blue-400" /></div>
          <div>
            <div className="metric-cell-label">Watch</div>
            <div className="metric-cell-value text-blue-400">{watchCount}</div>
          </div>
        </div>
        <div className="metric-cell">
          <div className="metric-cell-label">Total Exposure</div>
          <div className="metric-cell-value">{fmt(totalExposure)}</div>
        </div>
      </div>

      {/* Focus List Cards */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto pr-1 scrollbar-thin">
        {focusItems.length === 0 && (
          <div className="flex h-full items-center justify-center text-[12px] text-stone-500">
            No assets currently flagged for review.
          </div>
        )}
        {focusItems.map((item, i) => (
          <div key={`${item.dealId}-${item.reason}-${i}`} className={`rounded-lg border p-3 ${
            item.severity === 'Critical' ? 'border-red-400/20 bg-red-400/[0.03]'
            : item.severity === 'Elevated' ? 'border-amber-400/15 bg-amber-400/[0.02]'
            : 'border-stone-700/40 bg-black/20'
          }`}>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  {sevBadge(item.severity)}
                  <span className="text-[10px] font-mono text-stone-500">{item.dealId}</span>
                  <span className="text-[10px] rounded bg-stone-800 px-1.5 py-0.5 text-stone-400">{item.reason}</span>
                  {trendIcon(item.marketTrend)}
                </div>
                <div className="text-[13px] font-medium text-stone-200">{item.property}</div>
                <div className="text-[10px] text-stone-500">{item.location} &middot; {item.type} &middot; ${item.loanAmount.toFixed(1)}M</div>

                <div className="grid grid-cols-2 gap-3 mt-2">
                  <div className="rounded border border-stone-700/30 bg-stone-900/30 px-2.5 py-1.5">
                    <div className="text-[9px] font-mono uppercase text-stone-600">Current</div>
                    <div className="text-[11px] font-medium text-stone-200">{item.currentMetric}</div>
                  </div>
                  <div className="rounded border border-stone-700/30 bg-stone-900/30 px-2.5 py-1.5">
                    <div className="text-[9px] font-mono uppercase text-stone-600">Original</div>
                    {item.origMetric ? (
                      <div className="text-[11px] font-medium text-stone-400">{item.origMetric}</div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-2 text-[10px] text-stone-500">{item.notes}</div>
                <div className="mt-1.5 flex items-center gap-1">
                  <span className="text-[9px] font-mono uppercase text-amber-400/60">Action Required:</span>
                  <span className="text-[10px] text-stone-400">{item.actionRequired}</span>
                </div>
              </div>
              {item.addedDate ? (
                <div className="text-[9px] text-stone-600 whitespace-nowrap">Added {item.addedDate}</div>
              ) : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
